import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ipoService } from '../services/ipoService';
import LoadingSpinner from '../components/LoadingSpinner';
import Timeline from '../components/Timeline';

const IPODetailsPage = () => {
  const { id } = useParams();
  const [ipo, setIpo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);

  useEffect(() => {
    const fetchIPO = async () => {
      try {
        setLoading(true);
        const data = await ipoService.getIPOById(id);
        setIpo(data);
      } catch (err) {
        setError('IPO not found');
      } finally {
        setLoading(false);
      }
    };

    fetchIPO();
  }, [id]);

  const handleDownload = () => {
    alert('Download functionality would be implemented here');
  };

  const handleApplyNow = () => {
    alert('Apply Now functionality would be implemented here');
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="error-message">{error}</div>;
  if (!ipo) return <div className="error-message">IPO not found</div>;

  const truncatedDescription = ipo.description.length > 200 
    ? ipo.description.substring(0, 200) + '...' 
    : ipo.description;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/ipos" className="flex items-center text-gray-600 hover:text-blue-600 transition-colors">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to IPO List
            </Link>
            <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              IPO Tracker
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 md:p-8 mb-8">
          {/* Mobile Layout */}
          <div className="md:hidden">
            <div className="flex items-start space-x-4 mb-4">
              <img src={ipo.companyLogo} alt={ipo.companyName} className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
              <div className="flex-1">
                <h1 className="text-xl font-bold text-gray-900 mb-1">{ipo.companyName}</h1>
                <p className="text-gray-600 text-sm">{ipo.subtitle}</p>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button className="p-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors" onClick={handleDownload}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7,10 12,15 17,10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </button>
              <button className="flex-1 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all" onClick={handleApplyNow}>
                Apply Now
              </button>
            </div>
          </div>
          
          {/* Desktop Layout */}
          <div className="hidden md:flex justify-between items-start">
            <div className="flex items-center space-x-6">
              <img src={ipo.companyLogo} alt={ipo.companyName} className="w-20 h-20 rounded-xl object-cover" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{ipo.companyName}</h1>
                <p className="text-gray-600 text-lg">{ipo.subtitle}</p>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <button className="p-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors" onClick={handleDownload}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7,10 12,15 17,10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </button>
              <button className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all" onClick={handleApplyNow}>
                Apply Now
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">IPO Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm text-gray-600 mb-1">Issue Size</div>
              <div className="text-lg font-semibold text-gray-900">{ipo.issueSize}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm text-gray-600 mb-1">Price Range</div>
              <div className="text-lg font-semibold text-gray-900">{ipo.priceRange}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm text-gray-600 mb-1">Minimum Amount</div>
              <div className="text-lg font-semibold text-gray-900">{ipo.minInvestment}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm text-gray-600 mb-1">Lot Size</div>
              <div className="text-lg font-semibold text-gray-900">{ipo.lotSize} shares</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm text-gray-600 mb-1">Issue Dates</div>
              <div className="text-lg font-semibold text-gray-900">
                {new Date(ipo.issueDates.start).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} - 
                {new Date(ipo.issueDates.end).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm text-gray-600 mb-1">Listed On</div>
              <div className="text-lg font-semibold text-gray-900">{ipo.listedOn}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm text-gray-600 mb-1">Listed Price</div>
              <div className="text-lg font-semibold text-gray-900">{ipo.listedPrice}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm text-gray-600 mb-1">Listing Gains</div>
              <div className={`text-lg font-semibold ${
                ipo.gainsPositive ? 'text-green-600' : 'text-red-600'
              }`}>
                {ipo.listingGains}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">IPO Timeline</h2>
          <div className="hidden md:block">
            <Timeline timeline={ipo.timeline} isMobile={false} />
          </div>
          <div className="md:hidden">
            <Timeline timeline={ipo.timeline} isMobile={true} />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">About the Company</h2>
          <p className="text-gray-700 leading-relaxed">
            {showFullDescription ? ipo.description : truncatedDescription}
            {ipo.description.length > 200 && (
              <button 
                className="ml-2 text-blue-600 hover:text-blue-700 font-medium" 
                onClick={() => setShowFullDescription(!showFullDescription)}
              >
                {showFullDescription ? 'Read less' : 'Read more'}
              </button>
            )}
          </p>
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 md:hidden">
          <button className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl" onClick={handleApplyNow}>
            Apply Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default IPODetailsPage;